import { useCallback, useEffect, useRef, useState } from "react";
import { discardAmazonDraft, fetchAmazonProduct, postAmazonProductSync } from "../../api/client";
import type { AmzChange, AmzPublishResponse } from "../../types/amazon";
import { applyStringValue, baseAttrKey, issueTouchedByDraft } from "../../lib/amazonContent";
import { StateBanner } from "../../components/StateBanner";
import { Badge, Spinner, Thumb } from "./AmzUi";
import { CopilotPanel } from "./CopilotPanel";
import type { CopilotCommand } from "./CopilotPanel";
import { PublishModal } from "./PublishModal";
import { errorMessage } from "./helpers";
import type { DetailTab } from "./helpers";
import { useProductEditor } from "./useProductEditor";
import { HistoryTab } from "./tabs/HistoryTab";
import { ImagesTab } from "./tabs/ImagesTab";
import { IssuesTab } from "./tabs/IssuesTab";
import { OverviewTab } from "./tabs/OverviewTab";
import { PageTab } from "./tabs/PageTab";

interface ProductDetailProps {
  sku: string;
  tab: DetailTab;
  onTab: (t: DetailTab) => void;
  showToast: (msg: string) => void;
  /** overview（一覧・ナビのバッジ）を取り直す */
  onOverviewChanged: () => void;
  onProductName: (sku: string, name: string | null) => void;
}

const TABS: { key: DetailTab | "variations" | "aplus" | "premium" | "catalog"; label: string; off?: boolean }[] = [
  { key: "overview", label: "概要" },
  { key: "page", label: "商品ページ" },
  { key: "images", label: "画像" },
  { key: "issues", label: "Issues" },
  { key: "history", label: "履歴" },
  // 準備中のタブは末尾にまとめる（AIパネル表示中の狭い本文幅でも主要タブが隠れないように）
  { key: "variations", label: "バリエーション", off: true },
  { key: "aplus", label: "A+", off: true },
  { key: "premium", label: "Premium A+", off: true },
  { key: "catalog", label: "Amazon情報", off: true },
];

const SAVE_LABEL = { idle: "", saving: "保存中…", saved: "保存済み", error: "保存に失敗" } as const;

export function ProductDetail({ sku, tab, onTab, showToast, onOverviewChanged, onProductName }: ProductDetailProps) {
  const editor = useProductEditor(sku);
  const { detail } = editor;
  const [aiOpen, setAiOpen] = useState(true);
  const [aiTarget, setAiTarget] = useState<string | null>(null);
  const [command, setCommand] = useState<CopilotCommand | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const cmdSeq = useRef(1);

  const itemName = detail?.summary.itemName ?? null;
  const hasDetail = detail !== null;
  useEffect(() => {
    if (hasDetail) onProductName(sku, itemName);
  }, [hasDetail, sku, itemName, onProductName]);

  // タブバーが横スクロールになる幅でも、選択中のタブが見える位置にする
  useEffect(() => {
    document.querySelector(".amz-tab.on")?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [tab, hasDetail]);

  const openAi = useCallback((target: string | null) => {
    setAiOpen(true);
    setAiTarget(target);
    // AIパネルが詳細の下に積まれる幅では、見える位置まで移動する
    if (window.matchMedia("(max-width: 1280px)").matches) {
      setTimeout(() => document.getElementById("amz-ai-panel")?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
    }
  }, []);

  const runReview = () => {
    openAi(null);
    setCommand({ nonce: cmdSeq.current++, text: "AIで商品ページをレビュー", target: null });
  };

  const applyProposals = useCallback(
    (items: { field: string; newValue: string }[]) => {
      if (!detail) return;
      for (const it of items) {
        const def = detail.fields.fields.find((f) => f.key === baseAttrKey(it.field));
        if (!def) continue;
        editor.edit((d) => applyStringValue(d, it.field, it.newValue, def, detail.marketplaceId), [it.field], "AI");
      }
      showToast(items.length > 1 ? `${items.length}件を下書きに適用しました。` : "下書きに適用しました。Amazonにはまだ反映されていません。");
    },
    [detail, editor, showToast]
  );

  const sync = async () => {
    if (syncing || !detail) return;
    setSyncing(true);
    try {
      await editor.flush();
      const r = await postAmazonProductSync(sku);
      editor.replaceDetail(r.detail);
      setHistoryVersion((v) => v + 1);
      onOverviewChanged();
      showToast(
        r.conflicts.length > 0
          ? `Amazonの最新値を取得しました。未反映の下書き（${r.conflicts.length}件）は上書きしていません。`
          : "Amazonの最新値を取得しました。変更はありません。"
      );
    } catch (e) {
      showToast(`取得に失敗しました: ${errorMessage(e)}`);
    } finally {
      setSyncing(false);
    }
  };

  const discard = async () => {
    if (!window.confirm("下書きをすべて破棄して、Amazon現在値に戻します。よろしいですか？")) return;
    try {
      await editor.flush();
      await discardAmazonDraft(sku);
      const r = await fetchAmazonProduct(sku);
      editor.replaceDetail(r.detail);
      onOverviewChanged();
      showToast("下書きを破棄しました。");
    } catch (e) {
      showToast(`下書きを破棄できませんでした: ${errorMessage(e)}`);
    }
  };

  const onPublished = (res: AmzPublishResponse, changes: AmzChange[]) => {
    if (detail) {
      const resolved = detail.issues.filter((i) => issueTouchedByDraft(i, changes));
      if (resolved.length > 0) editor.markResolved(resolved);
    }
    editor.replaceDetail(res.detail);
    setHistoryVersion((v) => v + 1);
    onOverviewChanged();
  };

  if (editor.loading && !detail) {
    return (
      <main className="amz-main">
        <div className="amz-pad">
          <StateBanner kind="loading" message="商品情報を取得しています。" />
        </div>
      </main>
    );
  }
  if (editor.error || !detail) {
    return (
      <main className="amz-main">
        <div className="amz-pad">
          <StateBanner kind="error" message={editor.error ?? "商品情報を取得できませんでした。"} onRetry={editor.reload} />
        </div>
      </main>
    );
  }

  const n = editor.changes.length;
  const openIssues = detail.issues.filter((i) => !issueTouchedByDraft(i, editor.changes)).length;
  const syncDot = syncing ? "var(--amz-warn)" : detail.syncError ? "var(--amz-err)" : detail.registered ? "var(--amz-ok)" : "var(--amz-faint)";
  const syncLabel = syncing ? "取得中…" : detail.syncError ? "同期エラー" : detail.registered ? "同期済み" : "未登録";

  return (
    <>
      <main className="amz-main">
        <div className="amz-dhead">
          <div className="amz-dhead-row">
            <div className="amz-dhead-info">
              <Thumb url={detail.summary.mainImage} large />
              <div className="amz-dhead-text">
                <h1 className="amz-dtitle">{detail.summary.itemName ?? detail.sku}</h1>
                <div className="amz-dmeta">
                  <span>
                    SKU <span className="amz-mono">{detail.sku}</span>
                  </span>
                  <span>
                    ASIN <span className="amz-mono">{detail.asin ?? "—"}</span>
                  </span>
                  <span className="amz-dmeta-sync">
                    Amazon同期 <span className="amz-dot" style={{ background: syncDot }} />
                    <b>{syncLabel}</b>
                  </span>
                  {detail.syncError && <Badge tone="error" size="sm" title={detail.syncError}>取得失敗</Badge>}
                </div>
                {detail.syncError && <span className="amz-hint" style={{ color: "var(--amz-err)" }}>{detail.syncError}</span>}
              </div>
            </div>
            <div className="amz-dactions">
              <span className={`amz-save-state ${editor.saveState === "error" ? "err" : ""}`} role="status">
                {SAVE_LABEL[editor.saveState]}
              </span>
              <button type="button" className="amz-btn" onClick={() => void sync()} disabled={syncing || !detail.registered} title={detail.registered ? undefined : "Amazon未登録のため取得できません"}>
                {syncing ? (
                  <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    <Spinner size="sm" />
                    取得中…
                  </span>
                ) : (
                  "Amazonから最新取得"
                )}
              </button>
              <button type="button" className={`amz-btn ${aiOpen ? "toggle-on" : ""}`} onClick={() => setAiOpen((v) => !v)} aria-pressed={aiOpen}>
                AIパネル
              </button>
              <button type="button" className="amz-btn primary" disabled={n === 0} onClick={() => n > 0 && setModalOpen(true)}>
                変更を確認（{n}）
              </button>
            </div>
          </div>
          <div className="amz-tabs" role="tablist">
            {TABS.map((t) => {
              if (t.off) {
                return (
                  <button key={t.key} type="button" className="amz-tab off" role="tab" aria-disabled="true" title="準備中" tabIndex={-1}>
                    {t.label}
                  </button>
                );
              }
              const k = t.key as DetailTab;
              return (
                <button key={t.key} type="button" role="tab" aria-selected={tab === k} className={`amz-tab ${tab === k ? "on" : ""}`} onClick={() => onTab(k)}>
                  {t.label}
                  {k === "issues" && openIssues > 0 && <span className="amz-tab-badge">{openIssues}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="amz-dbody">
          {tab === "overview" && <OverviewTab detail={detail} editor={editor} onGoTab={onTab} />}
          {tab === "page" && (
            <PageTab
              detail={detail}
              editor={editor}
              aiOpen={aiOpen}
              aiTarget={aiTarget}
              onAi={(k) => openAi(k)}
              onReview={runReview}
              onDiscard={() => void discard()}
            />
          )}
          {tab === "images" && <ImagesTab detail={detail} editor={editor} showToast={showToast} />}
          {tab === "issues" && <IssuesTab detail={detail} editor={editor} onApply={(f, v) => applyProposals([{ field: f, newValue: v }])} showToast={showToast} />}
          {tab === "history" && <HistoryTab sku={sku} version={historyVersion} />}
        </div>
      </main>

      <CopilotPanel
        key={sku}
        open={aiOpen}
        sku={sku}
        productName={detail.summary.itemName ?? detail.sku}
        fields={detail.fields.fields}
        draft={editor.draft}
        target={aiTarget}
        onClearTarget={() => setAiTarget(null)}
        onClose={() => setAiOpen(false)}
        command={command}
        onApply={applyProposals}
      />

      {modalOpen && (
        <PublishModal
          detail={detail}
          flush={editor.flush}
          onClose={() => setModalOpen(false)}
          onPublished={onPublished}
          onGoHistory={() => {
            setModalOpen(false);
            onTab("history");
          }}
        />
      )}
    </>
  );
}
